import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { PAGE_NAMES, ROLES } from '../../../core/helpers/constants';
import { getRolesForPage } from '../../../core/helpers/utility';
import { IPermission } from '../../../core/models/auth.models';
import { ICampaign } from '../../../core/models/campaigns.model';
import { LoadDataFromServer } from '../../../shared/components/datatable/datatable.model';
import { AppService } from '../../../store/app.service';
import { CampaignsApiService } from '../campaigns.apiservice';
import { MergeCampaignsModal } from '../../../shared/modals/merge-campaign/merge-campaign.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UtilityService } from '../../../core/helpers/utility.service';
import { environment } from '../../../../environments/environment';
import { InstanceService } from '../../../core/instance/instance.service';
import { CampaignsService } from '../campaigns.service';
import { TranslateService } from '@ngx-translate/core';
import { ISearch } from '../../../core/models/search.model';

@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns-main.component.html',
  styleUrls: ['./campaigns-main.component.scss']
})
export class CampaignsMainComponent implements OnInit {
  doWhat: IPermission = {} as IPermission;
  columns: Array<any> = [];

  campaignsData: Array<any>
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private loadEvent!: LoadDataFromServer;
  dataTotalCount: number = 0;
  showDeleted: boolean = false;
  hideInactiveDefaultRoles = [1, 2, 3, 4, 5, 6, 10, 12];
  savedSearchId!: string;
  campaignSavedSearches: ISearch[] = [];
  selectedSavedSearchId: string | null = null;
  public userRoles: Array<number> = [];
  public ROLES = ROLES;

  constructor(
    private router: Router,
    private cApiService: CampaignsApiService,
    private modalService: NgbModal,
    private campaignsService: CampaignsService,
    private uService: UtilityService,
    private route: ActivatedRoute,
    private appService: AppService,
    private instance: InstanceService,
    private translate: TranslateService) {
    this.campaignsData = new Array<any>();
  }

  ngOnInit(): void {
    this.appService.setPageTitle$('CAMPAIGNS');

    this.columns = [
      { title: 'NAME', field: 'name', sortable: true, searchable: true },
      { title: 'CLIENT', field: 'clientName', sortable: true, searchable: true },
      { title: 'PLATFORM', field: 'platform', sortable: true, pipe: 'P_CampaignPlatformTitle', searchable: true },
      { title: 'DMS', field: 'dmsName', sortable: true, searchable: true },
      { title: 'STAGE', field: 'stageId', sortable: true, pipe: 'P_CampaignStageTitle', searchable: true },
      ...(!this.instance.isAltLayout ? [{ title: 'ACTIVE_SUBSCRIPTION', field: 'subscriptionActive', pipe: 'P_Boolean', sortable: true, searchable: true }] : []),
    ];

    this.userRoles = [...this.uService.getUserRoles()];
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Campaigns);
    this.savedSearchId = this.route.snapshot.queryParams['savedsearchid'];

    if (!this.savedSearchId && this.userRoles.some(role => this.hideInactiveDefaultRoles.includes(role))) {
      this.savedSearchId = this.defaultInactiveCampaignSearchId;
    }

    this.appService.fetchSearches();
    this.appService.getAllSearches$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((searches: ISearch[]) => {
        const userCampaignSearches = (searches || [])
          .filter(search => search.resultType?.toLowerCase() === 'campaign');
        const sharedSearch = {
          id: this.defaultInactiveCampaignSearchId,
          name: this.translate.instant('HIDE_INACTIVE_CAMPAIGNS'),
          resultType: 'Campaign'
        } as ISearch;
        this.campaignSavedSearches = [
          sharedSearch,
          ...userCampaignSearches.filter(search => search.id !== sharedSearch.id)
        ];
        this.selectedSavedSearchId = this.campaignSavedSearches.some(search => search.id === this.savedSearchId)
          ? this.savedSearchId
          : null;
      });
    this.fetchCampaigns();
  }

  fetchCampaigns() {
    this.appService.getAllCampaigns$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.campaignsData = response.data.map((campaign: any) => {
          if (campaign.stageId === 8) {
            campaign.statusColor = 'gray';
          }
          if (this.instance.isAltLayout && campaign.clientName) {
            campaign.clientName = campaign.clientName.replace('-- ', '');
          }

          return campaign;
        });
        this.dataTotalCount = response.totalCount;
      });
  }

  onLoadingData(event: LoadDataFromServer) {
    this.loadEvent = event;
    let search = this.loadEvent.search ? this.loadEvent.search : '';
    if (search !== '' && this.selectedSavedSearchId) {
      this.savedSearchId = '';
      this.selectedSavedSearchId = null;
    }
    this.appService.fetchAllCampaigns({
      SavedSearchId: this.savedSearchId,
      ShowDeleted: this.showDeleted,
      ...this.loadEvent
    });
  }

  onSavedSearchChange(savedSearchId: string | null) {
    this.selectedSavedSearchId = savedSearchId || null;
    this.savedSearchId = savedSearchId || '';
    this.appService.fetchAllCampaigns({
      SavedSearchId: this.savedSearchId,
      ShowDeleted: this.showDeleted,
      ...this.loadEvent
    });
  }

  onShowDeletedChange(){
    this.appService.fetchAllCampaigns({
      SavedSearchId: this.savedSearchId,
      ShowDeleted: this.showDeleted,
      ...this.loadEvent
    });
  }

  get defaultInactiveCampaignSearchId(): string {
    return environment.production
      ? 'b934ac9a-28dc-4b5d-8848-0eb0011bce73'
      : '2b606d1f-8a9a-4fb1-b951-f1e2f1a3a2d3';
  }

  editRow(evt: any) {
    const modalRef = this.campaignsService.openEditCampaignModal({ campaignId: evt.id });
    modalRef.closed.subscribe((savedCampaign?: ICampaign) => {
      if (savedCampaign?.id) {
        this.router.navigate(['/pages/campaign-details', savedCampaign.id]);
      }
    });
  }

  onAddCampaign() {
    const modalRef = this.campaignsService.openEditCampaignModal();
    modalRef.closed.subscribe((savedCampaign?: ICampaign) => {
      if (savedCampaign?.id) {
        this.router.navigate(['/pages/campaign-details', savedCampaign.id]);
      }
    });
  }

  deleteRows(evt: any) {
    this.cApiService.deleteCampaign(evt)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          Swal.fire(this.translate.instant('CAMPAIGNS'), this.translate.instant('CAMPAIGNS_DELETED'), 'success')
        }
        this.appService.fetchAllCampaigns({
          SavedSearchId: this.savedSearchId,
          ShowDeleted: this.showDeleted,
          ...this.loadEvent
        });
      })
  }

  getRowLink(row: any) {
    return `/pages/campaign-details/${row.id}`;
  }

  ngAfterViewInit() {

  }

  handleMergeRecordsClicked(evt: string[]) {
    let campaignsToMerge = this.campaignsData.filter(f => evt.includes(f.id));
    // let groupedClient = campaignsToMerge.reduce(
    //   (result:any, currentValue:any) => { 
    //     (result[currentValue['clientId']] = result[currentValue['clientId']] || []).push(currentValue);
    //     return result;
    //   }, {});

    let groupedPlatform = campaignsToMerge.reduce(
        (result:any, currentValue:any) => { 
          (result[currentValue['platform']] = result[currentValue['platform']] || []).push(currentValue);
          return result;
        }, {});

    if(Object.keys(groupedPlatform).length > 1){
      Swal.fire({
        title: this.translate.instant('CANNOT_MERGE'),
        text: this.translate.instant('SELECTED_CAMPAIGNS_MUST_HAVE_SAME_PLATFORM'),
        icon: 'error',
        showCancelButton: false,
        confirmButtonText: this.translate.instant('OK')
      }).then((result) => {
        
      })
    }
    else{
      let size = campaignsToMerge.length == 2 ? 'lg' : 'xl';

      const dialogRef = this.modalService.open(MergeCampaignsModal, { size: size });

      dialogRef.componentInstance.campaignsToMerge = campaignsToMerge;
      const dialogSubmitSubscription = dialogRef.componentInstance.oMergeComplete.subscribe((r: any) => {
        this.appService.fetchAllCampaigns({
          SavedSearchId: this.savedSearchId,
          ShowDeleted: this.showDeleted,
          ...this.loadEvent
        });
        dialogSubmitSubscription.unsubscribe();
      })
    }  
  }

  async export() {
    this.cApiService.export({
      SavedSearchId: this.savedSearchId,
      ShowDeleted: this.showDeleted,
      ...this.loadEvent
    }).then((x: any) => {
      var newBlob = new Blob([x], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const data = window.URL.createObjectURL(newBlob);
      var link = document.createElement('a');
      link.href = data;
      link.download = "campaigns.xlsx";
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
        link.remove();
      }, 100);
    });
  }
}
